import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CreditCard } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Billing = () => {
  const navigate = useNavigate();
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSubscriptionInfo();
  }, []);

  const loadSubscriptionInfo = async () => {
    try {
      const response = await axios.get(`${API}/subscription/info`, {
        withCredentials: true
      });
      setSubscriptionInfo(response.data);
    } catch (error) {
      toast.error('Failed to load subscription info');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const tierPrices = {
    essential: 9.99,
    standard: 19.99,
    plus: 39.99
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary/10 to-background py-12 px-6">
      <div className="container mx-auto max-w-4xl">
        <Button 
          data-testid="back-to-dashboard-btn"
          variant="ghost" 
          onClick={() => navigate('/dashboard')}
          className="mb-6 rounded-full"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Dashboard
        </Button>

        <div className="bg-card rounded-3xl shadow-soft p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <CreditCard className="h-10 w-10 text-primary" />
            <h1 className="text-4xl font-heading font-bold" data-testid="billing-title">Billing</h1>
          </div>

          {/* Current Plan */}
          <div className="mb-12">
            <h2 className="text-2xl font-heading font-semibold mb-4">Current Plan</h2>
            {subscriptionInfo?.tier && subscriptionInfo.tier !== 'none' ? (
              <div className="p-6 bg-primary/5 rounded-2xl border border-primary/20" data-testid="current-plan">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-heading font-bold capitalize">
                      {subscriptionInfo.tier} Plan
                    </h3>
                    <p className="text-muted-foreground mt-1">
                      Status: <span className="capitalize font-semibold">{subscriptionInfo.status}</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-heading font-bold text-primary">
                      ${tierPrices[subscriptionInfo.tier]}
                    </div>
                    <p className="text-sm text-muted-foreground">per month</p>
                  </div>
                </div>
                <div className="pt-4 border-t border-border/50">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Per Event Limit</p>
                      <p className="font-semibold">${subscriptionInfo.limits?.per_event || 0}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">30-Day Limit</p>
                      <p className="font-semibold">${subscriptionInfo.limits?.per_30_days || 0}</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 bg-muted/30 rounded-2xl border border-border text-center" data-testid="no-plan">
                <p className="text-muted-foreground mb-4">You don't have an active subscription</p>
                <Button 
                  data-testid="subscribe-btn"
                  onClick={() => navigate('/select-tier')}
                  className="btn-primary"
                >
                  Subscribe Now
                </Button>
              </div>
            )}
          </div>

          {/* Plan Options */}
          {subscriptionInfo?.tier && subscriptionInfo.tier !== 'none' && (
            <div className="mb-12">
              <h2 className="text-2xl font-heading font-semibold mb-4">Change Plan</h2>
              <p className="text-muted-foreground mb-6">
                Want to upgrade or downgrade? Contact us to modify your subscription.
              </p>
              <div className="flex gap-4">
                <Button 
                  data-testid="upgrade-btn"
                  onClick={() => navigate('/select-tier')}
                  className="btn-outline"
                >
                  View Plans
                </Button>
              </div>
            </div>
          )}

          {/* Payment Information */}
          <div>
            <h2 className="text-2xl font-heading font-semibold mb-4">Payment Information</h2>
            <div className="p-6 bg-muted/30 rounded-2xl border border-border">
              <p className="text-sm text-muted-foreground mb-4">
                Your payments are securely processed by Stripe. All subscription charges are handled automatically.
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payment Method</span>
                  <span className="font-medium">Card ending in ****</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Next Billing Date</span>
                  <span className="font-medium">
                    {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Cancel Section */}
          {subscriptionInfo?.tier && subscriptionInfo.tier !== 'none' && (
            <div className="mt-12 pt-8 border-t border-border">
              <h2 className="text-2xl font-heading font-semibold mb-4">Cancel Subscription</h2>
              <p className="text-muted-foreground mb-6">
                Need to cancel? We're sorry to see you go. Your coverage will remain active until the end of your billing period.
              </p>
              <Button 
                data-testid="cancel-subscription-btn"
                variant="destructive"
                className="rounded-full"
                onClick={() => toast.info('Please contact support to cancel your subscription')}
              >
                Cancel Subscription
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Billing;
